import { Box, Flex, Skeleton, Text, Tooltip, VStack, useOutsideClick } from '@chakra-ui/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import AddButton from 'components/AddButton';
import HelpComponent from 'components/CustomHelp';
import QuestionMarkIcon from 'components/Icons/QuestionMarkIcon';
import ErrorPage from 'pages/ErrorPage';
import { STATUS } from 'projectConstants';
import { handleExpandSideInput } from 'redux/BuilderModeSlice';
import {
  addDimensionItem,
  fetchDimensionItems,
  fetchIndicatorDetail,
  fetchIndicatorDetailType,
  fetchModelTime,
  geModelTimeStatus,
  getChartFilterItems,
  getDimensionItems,
  getIndicatorDetail,
  getIndicatorDetailStatus,
  getTableShimmer,
} from 'redux/IndicatorPageSlice';
import { getCurrentDimensionIndex } from 'redux/InputPageSlice';
import {
  FetchPlangPageIndicatorTableData,
  onErrorShow,
  // getBlockDetails,
  getmultiDimFiltersItems,
  getIndicatorTableData,
} from 'redux/PlanPageSlice';
import {
  FetchDataInput,
  getBaseScenario,
  getExpandValue,
  getPlannerBlock,
  getPlannerModel,
  getSelectedIndicators,
  handleToggleInputPanel,
} from 'redux/PlannerModeSlice';
import addNewDimensionItem from 'utils/AddNewDimItem';
import { AppDispatch } from 'utils/GlobalHelpers';
import EmptyData from 'pages/BuilderMode/IndicatorPageOPZ/EmptyData';

import { BUILDER_MODE_V1_TOKENS } from '../constants';

import BuilderModeInputTypeTabsV1 from './components/BuilderModeInputTypeTabsV1';
import InputPanelChartSectionV1 from './components/InputPanelChartSectionV1';
import InputPanelChartV1 from './components/InputPanelChartV1';
import InputPanelContainerV1 from './components/InputPanelContainerV1';
import InputPanelHeaderV1 from './components/InputPanelHeaderV1';
import BuilderModeIndicatorTableV1 from './table/BuilderModeIndicatorTableV1';

const BuilderModeIndicatorPageV1 = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentDriver = useSelector(getSelectedIndicators);
  const containerReference = useRef<HTMLDivElement>(null);
  const [innerHeight, setInnerHeight] = useState(0);
  const indicatorTableData = useSelector(getIndicatorTableData);
  const indicator = useSelector(getIndicatorDetail);
  const isExpand = useSelector(getExpandValue);
  // const blockDetails = useSelector(getBlockDetails);
  const filteredChartItems = useSelector(getChartFilterItems);
  const multiDimFiltersItems = useSelector(getmultiDimFiltersItems);

  useLayoutEffect(() => {
    if (containerReference.current) {
      const containerHeight = containerReference.current.offsetHeight;
      const containerPadding =
        Number.parseInt(window.getComputedStyle(containerReference.current).paddingLeft, 10) +
        Number.parseInt(window.getComputedStyle(containerReference.current).paddingRight, 10);
      setInnerHeight(containerHeight - containerPadding);
    }
  }, [containerReference.current]);

  const dimensionItems = useSelector(getDimensionItems);
  const { pathname } = useLocation();
  const { modelId, blockId } = useParams();
  const indicatorDetailStatus = useSelector(getIndicatorDetailStatus);
  const tabShimmer = useSelector(getTableShimmer);
  const modelTimeStatus = useSelector(geModelTimeStatus);
  const currentDimensionIndex = useSelector(getCurrentDimensionIndex);
  const baseScenario = useSelector(getBaseScenario);
  const plannerModel = useSelector(getPlannerModel);
  const plannerBlock = useSelector(getPlannerBlock);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const tooltipReference = useRef<HTMLDivElement>(null);

  const chartFilterDimId = indicatorTableData?.column_headers?.[0]?.is_dimension
    ? indicatorTableData.column_headers[0].id
    : null;

  const dimensionId = currentDriver?.data_input?.dimensions?.[currentDimensionIndex];

  useOutsideClick({
    ref: tooltipReference,
    handler: () => setIsTooltipOpen(false),
  });

  const fetchModelTimeData = () => {
    dispatch(fetchModelTime(Number(modelId)));
  };

  const fetchDimItemsData = () => {
    dispatch(
      fetchDimensionItems({
        dimensionId,
        parameters: {
          scenario_id: baseScenario?.id,
        },
      }),
    );
  };

  useEffect(() => {
    if (modelId && blockId) {
      fetchModelTimeData();
    }
  }, [pathname, currentDriver]);

  useEffect(() => {
    if (currentDriver && currentDriver?.data_input?.dimensions?.length > 0) {
      fetchDimItemsData();
    }
  }, [pathname, currentDriver?.id, baseScenario?.id, dimensionId]);

  useEffect(() => {
    if (modelId && blockId && currentDriver?.indicator_type?.toLowerCase() === 'input') {
      dispatch(
        FetchPlangPageIndicatorTableData({
          indicator_id: currentDriver?.id,
          parameters: {
            scenario_id: baseScenario?.id,
            format_excel_data: true,
          },
        }),
      ).then(() => {
        dispatch(
          fetchIndicatorDetail({
            currentDriver,
            parameters: {
              dim_id: chartFilterDimId,
              scenario_id: baseScenario?.id,
            },
          }),
        );
      });
    }
  }, [currentDriver, baseScenario?.id]);

  const [noDimChartData, setNoDimChartData] = useState<Record<string, Record<string, number>> | []>(
    [],
  );

  useEffect(() => {
    if (multiDimFiltersItems && multiDimFiltersItems.length > 0) {
      dispatch(
        fetchIndicatorDetailType({
          currentDriver: indicator,
          parameters: {
            dim_id: chartFilterDimId,
            scenario_id: baseScenario?.id,
            is_chart_show: true,
            filter_dim_items: JSON.stringify(filteredChartItems),
          },
        }),
      ).then(() => {
        dispatch(
          fetchIndicatorDetail({
            currentDriver: indicator,
            parameters: {
              dim_id: chartFilterDimId,
              scenario_id: baseScenario?.id,
              is_chart_show: true,
              filter_dim_items: JSON.stringify(filteredChartItems),
            },
          }),
        );
      });
    }
  }, [indicatorTableData?.type]);

  useEffect(() => {
    if (indicator && !indicator?.output?.dims) {
      const object: Record<string, Record<string, number>> = {};
      object[indicator.output?.name] = indicator.output?.total_values;
      setNoDimChartData(object);
    }
  }, [indicator]);

  useEffect(() => {
    if (indicatorDetailStatus === STATUS.FAILED || modelTimeStatus === STATUS.FAILED) {
      dispatch(onErrorShow(true));
    }
  }, [indicatorDetailStatus, modelTimeStatus]);

  const commonObjects =
    plannerBlock?.dimensions?.length > 1
      ? plannerModel?.dimensions?.filter((object1: { id: number; name: string }) =>
          plannerBlock?.dimensions?.some(
            (object2: { id: number; name: string }) =>
              object1.id === object2.id && object1.name !== 'Time',
          ),
        )
      : plannerModel?.dimensions?.filter((object1: { id: number }) =>
          plannerBlock?.dimensions?.some((object2: { id: number }) => object1.id === object2.id),
        );

  const closeSection = () => {
    dispatch(handleToggleInputPanel());
    dispatch(handleExpandSideInput(false));
  };

  const showInitialShimmer =
    indicatorDetailStatus !== STATUS.SUCCESS || modelTimeStatus !== STATUS.SUCCESS;

  if (indicatorDetailStatus === STATUS.FAILED || modelTimeStatus === STATUS.FAILED) {
    return (
      <InputPanelContainerV1 className='input-panel visible'>
        <InputPanelHeaderV1 title={indicator?.output?.name || 'Inputs'} onClose={closeSection} />
        <Flex align='center' justify='center' flex={1}>
          <ErrorPage style={{ height: '100%' }} planError />
        </Flex>
      </InputPanelContainerV1>
    );
  }

  if (currentDriver?.indicator_type?.toLowerCase() === 'calculation' || !currentDriver) {
    return (
      <InputPanelContainerV1 className='input-panel visible'>
        <InputPanelHeaderV1 title={indicator?.output?.name || 'Inputs'} onClose={closeSection} />
        <Flex flex={1} align='center' justify='center'>
          <EmptyData />
        </Flex>
      </InputPanelContainerV1>
    );
  }

  const refetchData = async () => {
    dispatch(
      fetchIndicatorDetail({
        currentDriver,
        parameters: {
          dim_id: chartFilterDimId,
          scenario_id: baseScenario?.id,
        },
      }),
    ).then(() => {
      dispatch(
        FetchPlangPageIndicatorTableData({
          indicator_id: currentDriver?.id,
          parameters: {
            scenario_id: baseScenario?.id,
            format_excel_data: true,
          },
        }),
      );
    });
  };

  const onAddDimensionItem = async () => {
    await dispatch(
      addDimensionItem({
        dimension_id: currentDriver?.data_input?.dimensions[currentDimensionIndex],
        request_body: addNewDimensionItem(dimensionItems?.items, baseScenario?.id),
        toaster: {
          successMessage: 'Successfully added',
          errorMessage: 'An item with this name already exists',
        },
      }),
    );
    await dispatch(
      FetchDataInput({
        indicator_id: currentDriver?.id,
        parameters: {
          scenario_id: baseScenario?.id,
          format_excel_data: true,
        },
      }),
    );
    await refetchData();
  };

  const showInputTypeTabs =
    currentDriver?.data_input?.dimensions && commonObjects && commonObjects.length > 0;
  const renderTableSectionLoader = () => (
    <Box
      w='100%'
      flex={1}
      minH={0}
      border='1px solid'
      borderColor={BUILDER_MODE_V1_TOKENS.border}
      borderRadius='12px'
      bg='white'
      p='12px'
    >
      <Flex direction='column' gap='10px'>
        <Skeleton
          startColor='rgba(139, 92, 246, 0.08)'
          endColor='rgba(139, 92, 246, 0.2)'
          h='44px'
          borderRadius='10px'
        />
        {[...new Array(5)].map((_, index) => (
          <Skeleton
            key={index}
            startColor='rgba(139, 92, 246, 0.08)'
            endColor='rgba(139, 92, 246, 0.2)'
            h='40px'
            borderRadius='8px'
          />
        ))}
      </Flex>
    </Box>
  );

  return (
    <InputPanelContainerV1 className='input-panel visible'>
      <InputPanelHeaderV1 title={indicator?.output?.name || 'Inputs'} onClose={closeSection} />

      {indicator?.help && (
        <Tooltip
          isOpen={isTooltipOpen}
          hasArrow
          arrowSize={10}
          bg='white'
          placement='right'
          boxShadow='rgba(0, 0, 0, 0.24) 0px 3px 8px'
          label={
            <VStack bg='white' p='3' maxW='300px' w='180px' gap={1}>
              <Text fontFamily='bold_roboto' fontSize='sm' color='black'>
                Help
              </Text>
              <Text fontSize='2xs' color='black' noOfLines={3}>
                {indicator.help}
              </Text>
            </VStack>
          }
        >
          <Box
            ref={tooltipReference}
            position='absolute'
            top='24px'
            left='24px'
            display='none'
            onClick={() => setIsTooltipOpen(!isTooltipOpen)}
          >
            <QuestionMarkIcon height={23} width={23} color={BUILDER_MODE_V1_TOKENS.textDark} />
          </Box>
        </Tooltip>
      )}

      <Flex direction='column' gap='20px' flex={1} minH={0} overflow='hidden' h='100%'>
        {showInitialShimmer ? (
          <Box
            h={innerHeight ? `${innerHeight}px` : '25vh'}
            minH='120px'
            maxH='190px'
            flexShrink={0}
          >
            <Skeleton
              startColor='rgba(139, 92, 246, 0.08)'
              endColor='rgba(139, 92, 246, 0.2)'
              h='100%'
              borderRadius='8px'
            />
          </Box>
        ) : (
          <InputPanelChartSectionV1>
            <Box ref={containerReference} w='100%' h='100%'>
              {indicator?.output?.dims ? (
                <InputPanelChartV1
                  showLabel
                  chartLabelData={indicator.output.total_values}
                  chartData={indicator.output?.dim_item_data}
                  chartType={indicator.output?.instrument_type}
                  dimId={indicator.output?.dims?.id}
                  formatProperties={indicator.output?.data_format_properties}
                />
              ) : (
                <InputPanelChartV1
                  showLabel
                  chartLabelData={indicator?.output?.total_values}
                  chartData={noDimChartData}
                  chartType={indicator?.output?.instrument_type}
                  formatProperties={indicator?.output?.data_format_properties}
                />
              )}
            </Box>
          </InputPanelChartSectionV1>
        )}

        {!showInitialShimmer && showInputTypeTabs && (
          <BuilderModeInputTypeTabsV1
            inputType={indicatorTableData?.type}
            indicatorId={indicator?.id}
            refetchRequest={refetchData}
          />
        )}

        {showInputTypeTabs && (showInitialShimmer || tabShimmer) && renderTableSectionLoader()}

        {!showInitialShimmer && showInputTypeTabs && !tabShimmer && (
          <Flex direction='column' flex={1} minH={0} overflow='hidden' w='100%'>
            <BuilderModeIndicatorTableV1
              refetchRequest={refetchData}
              isPercentage={indicator?.data_format?.toLowerCase()}
            />
            {dimensionItems?.items && isExpand && <AddButton onClick={onAddDimensionItem} />}
            {indicatorTableData?.help && isExpand && (
              <Box mt={4} pb={2}>
                <HelpComponent helpText={indicatorTableData.help} style={{ width: '100%' }} />
              </Box>
            )}
          </Flex>
        )}
      </Flex>
    </InputPanelContainerV1>
  );
};

export default BuilderModeIndicatorPageV1;
